import React, { useMemo, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { type LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'

import { Modal } from '../../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'

import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetIntegrationEntitiesQuery } from '../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../common/integrations'
import { toLocalISODate } from '../../../../common/dateUtils'

import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'
import { DueDateInput } from './DueDateInput'
import { CrmRichTextEditor } from './CrmRichTextEditor'
import { useCrmAssignee } from './useCrmAssignee'

const NO_CATEGORY_OPTION_ID = '__no_category__'

const PRIORITY_OPTIONS: SelectOption[] = [
  { id: 'Low', label: 'Low' },
  { id: 'Medium', label: 'Medium' },
  { id: 'High', label: 'High' },
]
const DEFAULT_PRIORITY_OPTION = PRIORITY_OPTIONS[1]

const NO_CATEGORY_OPTION: SelectOption = { id: NO_CATEGORY_OPTION_ID, label: 'Select category' }

export const WealthboxTaskActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const accountId = useAppSelector(selectAccountId)

  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useCrmEligibility(IntegrationServiceId.WEALTHBOX, results ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.WEALTHBOX,
    sourceObject: 'task',
    entityType: 'task',
    relatedToType: 'Contact',
    successToast: { title: 'Creating task', message: 'Wealthbox task creation started.' },
    errorToast: { title: 'Failed to create task', message: 'Could not start the Wealthbox task creation. Please try again.' },
  })

  const [subject, setSubject] = useState('')
  const descriptionEditorRef = useRef<LexicalEditor>(null)
  const [descriptionText, setDescriptionText] = useState('')
  const [dueDate, setDueDate] = useState<Date | null>(null)
  const [selectedPriority, setSelectedPriority] = useState<SelectOption>(DEFAULT_PRIORITY_OPTION)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)

  const { data: categoriesData } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.WEALTHBOX,
    entityType: 'task_categories',
  })

  // `zocksAssignedTo` is the current user (the task creator); the picked teammate only
  // supplies the external `assignedTo` for the CRM.
  const clientOwner: string | undefined = eligibility.status === 'ready' && eligibility.eligibleCount === 1 ? eligibility.eligibleTargets[0].ownerId : undefined
  const { selectedAssignee, selectedAssigneeExternalId, assigneeOptions, selectedAssigneeOption, handleAssigneeOptionSelected } = useCrmAssignee(
    IntegrationServiceId.WEALTHBOX,
    clientOwner,
  )
  const currentUser = useAppSelector(selectAuthenticatedUser)

  const categories = useMemo(() => categoriesData?.data?.taskCategories ?? [], [categoriesData])

  const categoryOptions: SelectOption[] = useMemo(() => {
    const seenIds = new Set<string>()
    const opts: SelectOption[] = []
    for (const category of categories) {
      if (category.id === null || category.id === undefined || !category.name) {
        continue
      }
      const id = String(category.id)
      if (seenIds.has(id)) {
        continue
      }
      seenIds.add(id)
      opts.push({ id, label: category.name })
    }
    return [NO_CATEGORY_OPTION, ...opts]
  }, [categories])

  const selectedCategoryOption: SelectOption = useMemo(() => {
    if (!selectedCategoryId) {
      return NO_CATEGORY_OPTION
    }
    return categoryOptions.find((o) => o.id === selectedCategoryId) ?? NO_CATEGORY_OPTION
  }, [categoryOptions, selectedCategoryId])

  const count = results?.length ?? 0
  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'} in Wealthbox`
  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  // Every input must be filled before the task can be created. `selectedPriority` always holds a value, so it is inherently set.
  const isFormValid =
    subject.trim().length > 0 && descriptionText.trim().length > 0 && dueDate !== null && selectedAssigneeExternalId !== null && selectedCategoryId !== null

  const handleCategoryOptionSelected = (option: SelectOption) => {
    setSelectedCategoryId(option.id === NO_CATEGORY_OPTION_ID ? null : option.id)
  }

  const handlePriorityOptionSelected = (option: SelectOption) => {
    setSelectedPriority(option)
  }

  const handleSave = async () => {
    if (!isFormValid) {
      return
    }

    const zocksTask: Record<string, unknown> = {
      task: subject.trim(),
      priority: selectedPriority.id,
      status: 'open',
    }
    const editor = descriptionEditorRef.current
    if (editor && descriptionText.trim().length > 0) {
      zocksTask.description = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
    }
    if (dueDate) {
      zocksTask.dueDate = toLocalISODate(dueDate)
    }
    if (selectedAssignee && selectedAssigneeExternalId) {
      // The picked teammate only supplies the external CRM assignee.
      zocksTask.assignedTo = selectedAssigneeExternalId
    }
    // `zocksAssignedTo` is the current user, required by integrations-service SaveTaskDetailsDTO.
    if (currentUser?.id) {
      zocksTask.zocksAssignedTo = {
        type: 'internal',
        id: currentUser.id,
        user: {
          name: currentUser.name,
          type: 'internal',
        },
      }
    }
    if (selectedCategoryId) {
      const category = categories.find((c) => String(c.id) === selectedCategoryId)
      if (category) {
        zocksTask.category = String(category.id)
      }
    }

    await save({ tasks: [zocksTask] })
  }

  const body =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='Wealthbox' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='Wealthbox' />
    ) : (
      <div className='d-flex flex-column gap-3'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <Form.Group controlId='wealthboxTaskSubject' className='d-flex flex-column'>
          <Form.Label className='bold'>
            Task name <span className='text-danger'>*</span>
          </Form.Label>
          <Form.Control value={subject} onChange={(e) => setSubject(e.target.value)} placeholder='Enter task name' disabled={saving} />
        </Form.Group>
        <Form.Group controlId='wealthboxTaskDescription' className='d-flex flex-column'>
          <Form.Label className='bold'>
            Task description <span className='text-danger'>*</span>
          </Form.Label>
          <CrmRichTextEditor ref={descriptionEditorRef} variant='task' onTextContentChange={setDescriptionText} placeholder={<>Enter task description…</>} />
        </Form.Group>
        <Row className='g-3'>
          <Form.Group as={Col} controlId='wealthboxTaskAssignee' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Assignee <span className='text-danger'>*</span>
            </Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={assigneeOptions}
              initialSelectedOption={selectedAssigneeOption}
              onOptionSelected={handleAssigneeOptionSelected}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='wealthboxTaskDueDate' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Due date <span className='text-danger'>*</span>
            </Form.Label>
            <DatePicker
              selected={dueDate}
              onChange={(date: Date | null) => setDueDate(date)}
              todayButton='Today'
              dateFormat='dd/MM/yyyy'
              disabled={saving}
              customInput={<DueDateInput />}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='wealthboxTaskPriority' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Priority <span className='text-danger'>*</span>
            </Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={PRIORITY_OPTIONS}
              initialSelectedOption={selectedPriority}
              onOptionSelected={handlePriorityOptionSelected}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='wealthboxTaskCategory' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Category <span className='text-danger'>*</span>
            </Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={categoryOptions}
              initialSelectedOption={selectedCategoryOption}
              onOptionSelected={handleCategoryOptionSelected}
            />
          </Form.Group>
        </Row>
        <CrmEligibilityNotice eligibility={eligibility} verbPhrase='create a task for' crmName='Wealthbox' />
      </div>
    )

  const primaryActionDisabled = noEligibleClients ? false : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || !isFormValid

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>Create task to {targetLabel}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Create task'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
